import type { Lesson } from './courses';

/**
 * Parcours : Plan Gates Management — 4 lecons.
 */
export const PLAN_GATES_MANAGEMENT_LESSONS: Lesson[] = [
  {
    slug: 'understand-gates',
    title: 'Comprendre les feature gates',
    duration: '4 min',
    glossaryTerms: ['feature-gate', 'plan', 'entitlement'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Les feature gates (ou entitlements) sont le mecanisme qui controle l'acces aux fonctionnalites en fonction du plan d'abonnement. Chaque gate est un verrou booleen ou numerique :\n\n- **Booleen** — La feature est activee ou desactivee (ex: acces aux analytics avancees)\n- **Numerique** — Un quota ou une limite (ex: nombre max de feedbacks par mois, nombre de sieges)\n\nLes gates sont evaluees cote serveur a chaque requete API et cote client pour l'affichage conditionnel. Le backend est la source de verite : meme si le frontend masque un bouton, l'API refuse l'acces si le gate n'est pas ouvert.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les feature gates sont le pilier du modele freemium :\n\n- **Monetisation** — Les features premium motivent l'upgrade\n- **Experience fluide** — L'utilisateur voit ce qu'il pourrait avoir (upsell naturel)\n- **Securite** — Impossible de contourner les restrictions cote frontend\n- **Flexibilite** — Modifier les gates sans deploiement (configuration admin)\n\nSans gates, il faudrait maintenir des applications separees par plan, ce qui est intenable.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Lister les features** — Identifier toutes les fonctionnalites gateables\n2. **Mapper features → plans** — Definir quel plan donne acces a quelle feature\n3. **Implementer les gates** — Cote API (middleware) et cote frontend (hooks)\n4. **Tester** — Verifier que chaque plan a exactement les features prevues\n5. **Documenter** — Maintenir la matrice features x plans a jour",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La matrice complète des features par plan est documentee dans `docs/business/PLANS-FEATURES-MATRIX-2026.md`. L'admin peut visualiser et ajuster les gates depuis **Tech > Plan Gates**. Le frontend utilise un hook `useGate('feature-name')` pour conditionner l'affichage.",
      },
    ],
    linkedModule: {
      label: 'Plan Gates',
      path: '/tech/plan-gates',
      icon: 'Settings',
    },
  },
  {
    slug: 'configure-gates-per-plan',
    title: 'Configurer les gates par plan',
    duration: '5 min',
    glossaryTerms: ['configuration', 'plan-gate', 'admin'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La configuration des gates se fait a deux niveaux :\n\n**Niveau global (defaut)** :\n- Chaque plan a une configuration par defaut des gates\n- Definie dans la matrice features x plans\n- Appliquee automatiquement a toute nouvelle organisation\n\n**Niveau organisation (override)** :\n- Un admin peut activer/desactiver un gate specifique pour une organisation\n- Utile pour les deals Enterprise sur mesure ou les periodes d'essai\n- L'override est prioritaire sur le defaut du plan\n\nExemple : le plan Starter inclut 5 sieges par defaut, mais un deal Enterprise peut en avoir 200.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "La configurabilite des gates a deux niveaux permet de :\n\n- **Standardiser** — Les plans Free a Team suivent la matrice par defaut\n- **Personnaliser** — Les deals Enterprise ont des configurations sur mesure\n- **Experimenter** — Activer temporairement une feature premium pour un prospect (trial)\n- **Corriger** — Desactiver rapidement une feature buguee sans deploiement\n\nC'est la separation entre politique commerciale (quels plans) et configuration technique (quels gates).",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Consulter la matrice** — Verifier la configuration par defaut du plan\n2. **Identifier le besoin** — Le client a-t-il besoin d'un override ?\n3. **Appliquer l'override** — Depuis Tech > Plan Gates, selectionner l'organisation\n4. **Valider** — Verifier que le client a acces aux features attendues\n5. **Documenter** — Noter la raison de l'override dans les notes de l'organisation",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "L'ecran **Tech > Plan Gates** affiche un tableau croise plans x features. L'admin peut cliquer sur une cellule pour activer/desactiver un gate. Un onglet 'Overrides' liste toutes les organisations avec des configurations personnalisees.",
      },
    ],
    linkedModule: {
      label: 'Plan Gates',
      path: '/tech/plan-gates',
      icon: 'Settings',
    },
  },
  {
    slug: 'quotas-numeric-limits',
    title: 'Quotas et limites numeriques',
    duration: '4 min',
    glossaryTerms: ['quota', 'limit', 'usage-tracking'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Les quotas sont des gates numeriques qui limitent l'utilisation d'une ressource. Les principaux quotas de Feedback Orchestrator :\n\n- **Sieges** — Nombre max d'utilisateurs dans l'organisation (Free: 1, Solo: 1, Starter: 5, Team: 20, Enterprise: illimite)\n- **Feedbacks/mois** — Volume de feedbacks analysables par mois\n- **Tokens IA/mois** — Nombre de tokens IA disponibles (hors BYOK)\n- **Projets** — Nombre de projets simultanement actifs\n- **Stockage** — Espace disponible pour les fichiers et medias\n\nChaque quota est mesure en temps reel et reinitialise au debut de chaque cycle de facturation.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les quotas sont essentiels pour :\n\n- **Proteger les marges** — Eviter qu'un client Free consomme autant qu'un Enterprise\n- **Encourager l'upgrade** — Quand un quota est atteint, proposer le plan superieur\n- **Prevenir les abus** — Limiter l'utilisation abusive des ressources\n- **Planifier la capacite** — Prevoir les besoins infra en fonction de l'usage\n\nUn bon systeme de quotas est transparent : l'utilisateur voit sa consommation et les limites de son plan.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definir les quotas** — Pour chaque plan, fixer les limites dans la matrice\n2. **Implementer le tracking** — Compter l'usage en temps reel cote backend\n3. **Afficher la consommation** — Dashboard utilisateur avec barres de progression\n4. **Gerer le depassement** — Bloquer l'action ou notifier selon la politique\n5. **Reinitialiser** — A chaque nouveau cycle de facturation, remettre les compteurs a zero",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le dashboard de l'organisation affiche les quotas avec des jauges visuelles (vert/jaune/rouge). L'admin peut voir la consommation detaillee dans **Operations > Quotas**. Des alertes automatiques sont envoyees quand un quota depasse 80%.",
      },
    ],
    linkedModule: {
      label: 'Plan Gates',
      path: '/tech/plan-gates',
      icon: 'Settings',
    },
  },
  {
    slug: 'test-restrictions',
    title: 'Tester les restrictions',
    duration: '3 min',
    glossaryTerms: ['testing', 'impersonation', 'sandbox'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Tester les restrictions des feature gates est crucial pour garantir une experience coherente. Trois methodes de test :\n\n**1. Impersonation** :\n- L'admin se connecte 'en tant que' une organisation avec un plan specifique\n- Voit exactement ce que le client voit, gates inclus\n\n**2. Sandbox plan** :\n- Creer une organisation de test avec le plan a verifier\n- Executer le parcours utilisateur complet\n\n**3. Gate simulator** :\n- Depuis l'admin, activer/desactiver manuellement des gates\n- Verifier le comportement du frontend et de l'API en temps reel",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Un gate mal configure peut avoir des consequences graves :\n\n- **Sur-permission** — Un client Free accede a des features premium (perte de revenus)\n- **Sous-permission** — Un client payant est bloque injustement (churn et mecontentement)\n- **Incoherence** — Le frontend affiche un bouton mais l'API refuse l'action (UX cassee)\n\nTester systematiquement chaque plan apres toute modification de gates evite ces scenarios.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Preparer les cas de test** — Lister chaque feature pour chaque plan (autorise/interdit)\n2. **Creer les comptes test** — Un compte par plan (Free, Solo, Starter, Team)\n3. **Executer les tests** — Pour chaque plan, verifier chaque feature\n4. **Valider l'API** — Envoyer les requetes API et verifier les codes 200/403\n5. **Valider le frontend** — Verifier que les elements sont affiches/masques correctement\n6. **Documenter** — Creer un rapport de test avec les resultats",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "L'outil de test est accessible dans **Tech > Plan Gates > Mode Test**. L'admin peut selectionner un plan et simuler les gates sans affecter les vrais clients. Un rapport automatique compare les gates actifs avec la matrice de reference et signale les ecarts.",
      },
    ],
    linkedModule: {
      label: 'Plan Gates',
      path: '/tech/plan-gates',
      icon: 'Settings',
    },
  },
];
